import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download, Printer, Share2, Star } from 'lucide-react';
import Badge from '../components/ui/Badge.jsx';
import { getArticle } from '../lib/auth';
import { formatArticleType, normalizeArticle } from '../lib/journalFormat';

function displayDate(value) {
  if (!value) return 'Pending';
  return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(value));
}

function ArticleDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getArticle(id)
      .then((result) => {
        const nextArticle = normalizeArticle(result.data);
        setArticle(nextArticle);
        document.title = `${nextArticle.title} - JAMSD`;
        setMessage('');
      })
      .catch((error) => setMessage(error.message))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <>
      <section className="bg-white pt-24 pb-5 border-b border-bg-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <p className="font-sans text-slate text-xs font-semibold uppercase tracking-[0.35em]">Published Article</p>
            <h1 className="font-display text-navy text-2xl italic font-bold mt-1">Journal of Applied Medical & Surgical Disciplines</h1>
          </div>
          <div className="font-sans text-sm text-slate md:text-right">
            <p>ISSN 2XXX-XXXX (Print) · 2XXX-XXXY (Online)</p>
            <p>{article?.volume || 'Article'}{article?.pageRange ? ` · Pages ${article.pageRange}` : ''}</p>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading && <div className="rounded border border-bg-border bg-white p-8 text-center font-sans text-slate">Loading article...</div>}
        {message && <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{message}</div>}
        {article && (
          <article className="bg-white p-4">
            <nav className="font-sans text-sm text-slate mb-5">
              <Link to="/" className="hover:text-amber">Home</Link>
              <span> / </span>
              <Link to="/issues" className="hover:text-amber">Archives</Link>
              <span> / </span>
              <span>{article.volume}</span>
              <span> / Article</span>
            </nav>

            <Badge type={article.type} className="uppercase tracking-wide rounded" />
            <h1 className="font-display text-navy text-3xl md:text-4xl font-bold leading-tight mt-5 max-w-6xl">{article.title}</h1>

            {article.imageUrl && (
              <div className="mt-6 max-w-4xl overflow-hidden rounded-xl border border-bg-border">
                <img src={article.imageUrl} alt={article.title} className="max-h-96 w-full object-cover" />
              </div>
            )}

            {/* <div className="mt-5 space-y-2">
              <p className="font-sans text-lg text-navy font-semibold">
                {article.authorList.map((author, index) => (
                  <span key={`${author.email || author.name}-${index}`}>
                    {author.name}{author.isCorresponding ? '*' : ''}{index < article.authorList.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </p>
              <div className="font-sans text-sm text-slate space-y-1">
                {article.authorList.map((author, index) => (
                  <p key={`${author.affiliation}-${index}`}><sup>{index + 1}</sup> {author.affiliation || 'Affiliation pending'}</p>
                ))}
                {article.authorList.find((author) => author.isCorresponding)?.email && (
                  <p>* Corresponding: {article.authorList.find((author) => author.isCorresponding).email}</p>
                )}
              </div>
            </div> */}

            <div className="mt-6">
  <div className="space-y-4">
    {article.authorList.map((author, index) => (
      <div
        key={`${author.email || author.name}-${index}`}
        className="rounded-xl border border-bg-border bg-bg-ice p-4"
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          
          <div className="flex-1">
            <div className="flex items-center flex-wrap gap-2">
              <h3 className="font-sans text-lg font-semibold text-navy">
                {author.name}
              </h3>

              {author.isCorresponding && (
                <span className="rounded-full bg-amber/10 px-3 py-1 text-xs font-semibold text-amber">
                  Corresponding Author
                </span>
              )}
            </div>

            <p className="mt-2 font-sans text-sm text-slate leading-relaxed">
              {author.affiliation || 'Affiliation pending'}
            </p>

            {author.email && (
              <p className="mt-2 font-sans text-sm">
                <span className="text-slate">Email:</span>{' '}
                <a
                  href={`mailto:${author.email}`}
                  className="text-navy font-medium hover:text-amber break-all"
                >
                  {author.email}
                </a>
              </p>
            )}
          </div>

          <div className="shrink-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-bg-border font-semibold text-navy">
              {index + 1}
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
</div>

            <div className="flex flex-wrap gap-8 border-b border-bg-border py-5 font-sans text-sm text-slate">
              <span>Published: {displayDate(article.publishedAt)}</span>
              <span>CC BY 4.0</span>
            </div>

            <div className="flex flex-wrap gap-3 py-5">
              {article.pdfUrl && <a href={article.pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-amber px-5 py-2 inline-flex items-center gap-2"><Download className="h-4 w-4" />Download PDF</a>}
              <button type="button" className="rounded border border-bg-border px-5 py-2 font-sans font-semibold text-navy">Cite</button>
              <button type="button" className="rounded border border-bg-border px-5 py-2 font-sans font-semibold text-navy inline-flex items-center gap-2"><Share2 className="h-4 w-4" />Share</button>
              <button type="button" className="rounded border border-bg-border px-5 py-2 font-sans font-semibold text-navy inline-flex items-center gap-2"><Star className="h-4 w-4" />Save</button>
              <button type="button" onClick={() => window.print()} className="rounded border border-bg-border px-5 py-2 font-sans font-semibold text-navy inline-flex items-center gap-2"><Printer className="h-4 w-4" />Print</button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Metric value={article.views} label="Views" />
              <Metric value={article.downloads} label="Downloads" />
              <Metric value="0" label="Citations" />
              <Metric value="—" label="Altmetric" />
            </div>

            <Section title="Abstract">
              <div className="prose max-w-none font-sans text-gray-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: article.abstractHtml || '<p>Abstract not available.</p>' }} />
            </Section>

            <Section title="Keywords">
              <div className="flex flex-wrap gap-2">
                {article.keywords.length ? article.keywords.map((keyword) => (
                  <span key={keyword} className="rounded-full border border-bg-border bg-bg-ice px-4 py-1 text-sm font-sans font-semibold text-slate">{keyword}</span>
                )) : <p className="font-sans text-slate">No keywords listed.</p>}
              </div>
              <p className="mt-5 inline-block rounded bg-green-50 px-4 py-2 font-sans text-sm font-semibold text-green-700">
                Open Access — Creative Commons Attribution 4.0 International (CC BY 4.0)
              </p>
            </Section>

            <Section title="Manuscript Details">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-sans text-sm">
                <Detail label="Article Type" value={formatArticleType(article.type)} />
                <Detail label="Issue" value={article.volume} />
                <Detail label="Pages" value={article.pageRange || 'Pending'} />
              </div>
            </Section>
          </article>
        )}
      </main>
    </>
  );
}

function Metric({ value, label }) {
  return (
    <div className="rounded bg-bg-ice p-5 text-center">
      <p className="font-display text-navy text-3xl font-bold">{value}</p>
      <p className="font-sans text-xs uppercase tracking-wide text-slate mt-1">{label}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border-t border-bg-border py-6">
      <h2 className="font-sans text-sm uppercase tracking-[0.3em] text-slate font-bold mb-4">{title}</h2>
      {children}
    </section>
  );
}

function Detail({ label, value }) {
  return (
    <div className="rounded border border-bg-border bg-bg-ice p-4">
      <p className="text-xs uppercase tracking-wide text-slate">{label}</p>
      <p className="font-semibold text-navy mt-1">{value}</p>
    </div>
  );
}

export default ArticleDetail;
